<svelte:options runes={true} />

<script lang="ts">
  import { verifyPassword } from '@dev';
  import { isProduction, SHOW_INTRO_IN_DEV } from '@env';
  import { prefs } from '@reactive';

  // Check if we should show the intro (production build or dev flag enabled)
  export const shouldShowIntro = isProduction() || SHOW_INTRO_IN_DEV;

  // callback supplied by the parent
  let { getStarted } = $props<{ getStarted?: () => void }>();

  let password = $state('');
  let errorMessage = $state('');
  let isValidating = $state(false);

  async function handleSubmit(event: Event) {
    event.preventDefault();

    if (!password.trim()) {
      errorMessage = 'Please enter a password';
      return;
    }

    isValidating = true;
    errorMessage = '';

    try {
      const isValid = await verifyPassword(password);

      if (isValid) {
        // Store expiry = now + 1 hour (in seconds since epoch)
        const nowSeconds = Math.floor(Date.now() / 1000);
        prefs.demoPasswordExpiry = nowSeconds + 3600;
        getStarted?.(); // invoke the parent callback
      } else {
        errorMessage = 'Oh, a wiseguy! You think you can just waltz in?';
      }
    } catch (error) {
      console.error('Password validation error:', error);
      errorMessage = 'An error occurred during validation. Please try again.';
    } finally {
      isValidating = false;
    }
  }
</script>

<style>
  .intro-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #1a0a02; /* Almost Black with slight red/brown tint */
    z-index: 1000;
  }

  .intro-content {
    text-align: center;
    padding: 2rem;
    border-radius: 8px;
    background-color: rgba(0, 0, 0, 0.7);
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
    max-width: 530px; /* Increase by ~33% from original 400px */
    color: #fff;
  }

  .image-container {
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
    margin-bottom: 1.5rem;
    width: 100%;
  }

  .speakeasy-image {
    width: 100%; /* Fill the container */
    display: block;
  }

  h1 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    color: #fff;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  }

  .password-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .input-group {
    display: flex;
    width: 100%;
  }

  input {
    padding: 0.75rem;
    width: 100%;
    border: 1px solid #333;
    border-radius: 4px;
    font-size: 1rem;
    background-color: rgba(255, 255, 255, 0.9);
    color: #000;
  }

  input:focus {
    outline: none;
    border-color: #fff;
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
  }

  input::placeholder {
    color: #666;
  }

  .error-message {
    color: #ff6b6b;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    text-align: center;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  }

  .submit-button {
    padding: 0.75rem 1.5rem;
    font-size: 1.1rem;
    background-color: #b8860b; /* Dark Golden Rod */
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;
    text-transform: uppercase;
    font-weight: bold;
    letter-spacing: 1px;
  }

  .submit-button:hover:not(:disabled) {
    background-color: #daa520; /* Golden Rod */
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  }

  .submit-button:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: none;
  }

  .submit-button:disabled {
    background-color: #5f4a35;
    cursor: not-allowed;
    opacity: 0.7;
  }
</style>

<div class="intro-container">
  <div class="intro-content">
    <div class="image-container">
      <img class="speakeasy-image" alt="Speakeasy entrance" src="/speakeasy.jpg" />
    </div>
    <h1>What&rsquo;s the password, bub?</h1>

    <form class="password-form" onsubmit={handleSubmit}>
      <div class="input-group">
        <input
          id="password"
          autocomplete="current-password"
          placeholder="Um..."
          type="password"
          bind:value={password}
        />
      </div>

      {#if errorMessage}
        <div class="error-message">{errorMessage}</div>
      {/if}

      <button class="submit-button" disabled={isValidating} type="submit">
        {isValidating ? 'Checking...' : 'Enter'}
      </button>
    </form>
  </div>
</div>
